// FILE: src/components/FlowBuilder/properties/ApiProperties.jsx
import React, { useState, useEffect } from 'react';
import {
  VStack,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  Button,
  Text,
  Divider,
  HStack,
  IconButton,
  Box,
  Switch,
  Radio,
  RadioGroup,
  Stack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Alert,
  AlertIcon,
  Code,
  Tag
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon, InfoIcon } from '@chakra-ui/icons';
import { LuBraces } from 'react-icons/lu';
import VariablePickerDrawer from '../VariablePickerDrawer';
import JSONBodyBuilder from './JSONBodyBuilder';

export default function ApiProperties({ nodeConfig, setNodeConfig, updateNodeData, nodes = [] }) {
  // Local state for array-based fields
  const [headers, setHeaders] = useState(nodeConfig.headers || [{ key: '', value: '' }]);
  const [responseMapping, setResponseMapping] = useState(nodeConfig.responseMapping || [{ responseField: '', saveAs: '' }]);
  const [sampleJson, setSampleJson] = useState(nodeConfig.sampleJson || '');
  const [generatedFields, setGeneratedFields] = useState(nodeConfig.contractFields || []);

  // Variable picker state
  const [varPickerOpen, setVarPickerOpen] = useState(false);
  // activeVarTarget: { type: 'queryParam'|'header'|'body'|'endpoint'|'bodyField', index?: number, callback?: fn }
  const [activeVarTarget, setActiveVarTarget] = useState(null);

  const openVarPicker = (type, index, callback) => {
    setActiveVarTarget({ type, index, callback });
    setVarPickerOpen(true);
  };

  const handleVarSelect = (varPath) => {
    const token = `{{${varPath}}}`;
    if (!activeVarTarget) return;
    const { type, index, callback } = activeVarTarget;

    if (type === 'queryParam') {
      const newParams = [...(nodeConfig.queryParams || [])];
      newParams[index] = { ...newParams[index], value: (newParams[index].value || '') + token };
      setNodeConfig({ ...nodeConfig, queryParams: newParams });
    } else if (type === 'header') {
      const newHdrs = [...headers];
      newHdrs[index] = { ...newHdrs[index], value: (newHdrs[index].value || '') + token };
      setHeaders(newHdrs);
      setNodeConfig({ ...nodeConfig, headers: newHdrs });
    } else if (type === 'body') {
      setNodeConfig({ ...nodeConfig, apiPayload: (nodeConfig.apiPayload || '') + token });
    } else if (type === 'endpoint') {
      setNodeConfig({ ...nodeConfig, apiEndpoint: (nodeConfig.apiEndpoint || '') + token });
    } else if (type === 'bodyField' && callback) {
      callback(token);
    }
    setVarPickerOpen(false);
  };

  // ====== Sync local state when nodeConfig changes (critical for prefill) ======
  useEffect(() => {
    setHeaders(nodeConfig.headers || [{ key: '', value: '' }]);
  }, [nodeConfig.headers]);

  useEffect(() => {
    setResponseMapping(nodeConfig.responseMapping || [{ responseField: '', saveAs: '' }]);
  }, [nodeConfig.responseMapping]);

  useEffect(() => {
    setSampleJson(nodeConfig.sampleJson || '');
  }, [nodeConfig.sampleJson]);

  useEffect(() => {
    setGeneratedFields(nodeConfig.contractFields || []);
  }, [nodeConfig.contractFields]);

  // --- Header helpers ---
  const addHeader = () => {
    const newHeaders = [...headers, { key: '', value: '' }];
    setHeaders(newHeaders);
    setNodeConfig({ ...nodeConfig, headers: newHeaders });
  };

  const removeHeader = (index) => {
    const newHeaders = headers.filter((_, i) => i !== index);
    setHeaders(newHeaders);
    setNodeConfig({ ...nodeConfig, headers: newHeaders });
  };

  const updateHeader = (index, field, value) => {
    const newHeaders = [...headers];
    newHeaders[index][field] = value;
    setHeaders(newHeaders);
    setNodeConfig({ ...nodeConfig, headers: newHeaders });
  };

  // --- Response Mapping helpers ---
  const addResponseMapping = () => {
    const newMapping = [...responseMapping, { responseField: '', saveAs: '' }];
    setResponseMapping(newMapping);
    setNodeConfig({ ...nodeConfig, responseMapping: newMapping });
  };

  const removeResponseMapping = (index) => {
    const newMapping = responseMapping.filter((_, i) => i !== index);
    setResponseMapping(newMapping);
    setNodeConfig({ ...nodeConfig, responseMapping: newMapping });
  };

  const updateResponseMapping = (index, field, value) => {
    const newMapping = [...responseMapping];
    newMapping[index][field] = value;
    setResponseMapping(newMapping);
    setNodeConfig({ ...nodeConfig, responseMapping: newMapping });
  };

  // --- Generate fields from sample JSON ---
  const generateFieldsFromJson = () => {
    try {
      const parsed = JSON.parse(sampleJson);
      const fields = [];
      const extractFields = (obj, prefix = '') => {
        Object.keys(obj).forEach((key) => {
          const value = obj[key];
          const path = `.${key}`;
          let type = typeof value;
          if (Array.isArray(value)) type = 'array';
          else if (value === null) type = 'string';
          else if (typeof value === 'object') type = 'object';

          fields.push({
            fieldName: key,
            jsonPath: path,
            type,
            example: typeof value === 'object' ? JSON.stringify(value) : String(value),
          });

          if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
            extractFields(value, path);
          }
        });
      };
      extractFields(parsed);
      setGeneratedFields(fields);
      setNodeConfig({ ...nodeConfig, contractFields: fields, sampleJson });
    } catch (e) {
      alert('Invalid JSON format');
    }
  };

  return (
    <VStack spacing={4} align="stretch">
      {/* Info Banner */}
      <Alert status="info" variant="subtle" borderRadius="md" fontSize="xs">
        <AlertIcon boxSize="14px" />
        <Text fontSize="xs">
          <strong>Enhanced API Node</strong> — Connect to external systems with dynamic variable mapping
        </Text>
      </Alert>

      <Accordion allowMultiple defaultIndex={[0, 1]} >
        {/* ===== SECTION 1: Basic Configuration ===== */}
        <AccordionItem >
          <AccordionButton px={0}>
            <Box flex="1" textAlign="left" fontSize="sm" fontWeight="semibold">
              Basic Configuration
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel pb={4} px={0}>
            <VStack spacing={3} align="stretch">
              <FormControl>
                <FormLabel fontSize="sm">Step Name</FormLabel>
                <Input
                  size="sm"
                  value={nodeConfig.label || ''}
                  onChange={(e) => setNodeConfig({ ...nodeConfig, label: e.target.value })}
                  placeholder="e.g., Validate Credit Score"
                />
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">HTTP Method</FormLabel>
                <Select
                  size="sm"
                  value={nodeConfig.apiMethod || 'POST'}
                  onChange={(e) => setNodeConfig({ ...nodeConfig, apiMethod: e.target.value })}
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="PATCH">PATCH</option>
                  <option value="DELETE">DELETE</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Endpoint URL</FormLabel>
                <Input
                  size="sm"
                  value={nodeConfig.apiEndpoint || ''}
                  onChange={(e) => setNodeConfig({ ...nodeConfig, apiEndpoint: e.target.value })}
                  placeholder="https://api.example.com/endpoint"
                  fontFamily="mono"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Supports variables: <Code fontSize="xs">{`https://api.com/users/{{customer.id}}`}</Code>
                </Text>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Authentication Type</FormLabel>
                <Select
                  size="sm"
                  value={nodeConfig.authType || 'none'}
                  onChange={(e) => setNodeConfig({ ...nodeConfig, authType: e.target.value })}
                >
                  <option value="none">None</option>
                  <option value="apikey">API Key</option>
                  <option value="bearer">Bearer Token</option>
                  <option value="basic">Basic Auth</option>
                </Select>
              </FormControl>

              {nodeConfig.authType === 'apikey' && (
                <>
                  <FormControl>
                    <FormLabel fontSize="sm">Header Key</FormLabel>
                    <Input
                      size="sm"
                      value={nodeConfig.authHeaderKey || 'X-API-Key'}
                      onChange={(e) => setNodeConfig({ ...nodeConfig, authHeaderKey: e.target.value })}
                      placeholder="X-API-Key"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">API Key Value</FormLabel>
                    <Input
                      size="sm"
                      type="password"
                      value={nodeConfig.apiKey || ''}
                      onChange={(e) => setNodeConfig({ ...nodeConfig, apiKey: e.target.value })}
                      placeholder="Enter your API key"
                      fontFamily="mono"
                    />
                  </FormControl>
                </>
              )}

              {nodeConfig.authType === 'bearer' && (
                <FormControl>
                  <FormLabel fontSize="sm">Bearer Token</FormLabel>
                  <Input
                    size="sm"
                    type="password"
                    value={nodeConfig.bearerToken || ''}
                    onChange={(e) => setNodeConfig({ ...nodeConfig, bearerToken: e.target.value })}
                    placeholder="Enter bearer token"
                    fontFamily="mono"
                  />
                </FormControl>
              )}

              {nodeConfig.authType === 'basic' && (
                <>
                  <FormControl>
                    <FormLabel fontSize="sm">Username</FormLabel>
                    <Input
                      size="sm"
                      value={nodeConfig.basicUsername || ''}
                      onChange={(e) => setNodeConfig({ ...nodeConfig, basicUsername: e.target.value })}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Password</FormLabel>
                    <Input
                      size="sm"
                      type="password"
                      value={nodeConfig.basicPassword || ''}
                      onChange={(e) => setNodeConfig({ ...nodeConfig, basicPassword: e.target.value })}
                    />
                  </FormControl>
                </>
              )}

              {nodeConfig.authType === 'none' && (
                <FormControl>
                  <FormLabel fontSize="sm">API Key (Optional)</FormLabel>
                  <Input
                    size="sm"
                    type="password"
                    value={nodeConfig.apiKey || ''}
                    onChange={(e) => setNodeConfig({ ...nodeConfig, apiKey: e.target.value })}
                    placeholder="Enter API key if needed"
                    fontFamily="mono"
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Sent as Authorization header
                  </Text>
                </FormControl>
              )}
            </VStack>
          </AccordionPanel>
        </AccordionItem>

        {/* ===== SECTION 2: Execution Mode ===== */}
        <AccordionItem>
          <AccordionButton px={0}>
            <Box flex="1" textAlign="left" fontSize="sm" fontWeight="semibold">
              Execution Mode
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel pb={4} px={0}>
            <RadioGroup
              value={nodeConfig.executionMode || 'sync'}
              onChange={(value) => setNodeConfig({ ...nodeConfig, executionMode: value })}
            >
              <Stack spacing={3}>
                <Box p={3} border="1px" borderColor="gray.200" borderRadius="md" cursor="pointer">
                  <Radio value="async" size="sm">
                    <Text fontSize="sm" fontWeight="medium">🔄 Fire & Continue (Async)</Text>
                  </Radio>
                  <Text fontSize="xs" color="gray.500" ml={6}>
                    Send request and continue flow immediately without waiting
                  </Text>
                </Box>
                <Box p={3} border="1px" borderColor="gray.200" borderRadius="md" cursor="pointer">
                  <Radio value="sync" size="sm">
                    <Text fontSize="sm" fontWeight="medium">⚡ Call & Wait (Sync)</Text>
                  </Radio>
                  <Text fontSize="xs" color="gray.500" ml={6}>
                    Wait for response before proceeding
                  </Text>
                </Box>
              </Stack>
            </RadioGroup>

            {nodeConfig.executionMode === 'sync' && (
              <Box mt={3} p={3} bg="blue.50" borderRadius="md" border="1px" borderColor="blue.200">
                <Text fontSize="xs" fontWeight="semibold" mb={2}>Sync Mode Settings</Text>
                <VStack spacing={2} align="stretch">
                  <FormControl>
                    <FormLabel fontSize="xs">Response Timeout (seconds)</FormLabel>
                    <Input
                      size="xs"
                      type="number"
                      value={nodeConfig.timeout || 30}
                      onChange={(e) => setNodeConfig({ ...nodeConfig, timeout: parseInt(e.target.value) || 30 })}
                      min={1}
                      max={300}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="xs">Save Response As</FormLabel>
                    <Input
                      size="xs"
                      value={nodeConfig.responseKey || 'api_response'}
                      onChange={(e) => setNodeConfig({ ...nodeConfig, responseKey: e.target.value })}
                      fontFamily="mono"
                    />
                    <Text fontSize="10px" color="blue.600" mt={1}>
                      Access via: <Code fontSize="10px">{`{{steps.${nodeConfig.responseKey || 'api_response'}.data}}`}</Code>
                    </Text>
                  </FormControl>
                </VStack>
              </Box>
            )}
          </AccordionPanel>
        </AccordionItem>

        {/* ===== SECTION 3: Query Parameters ===== */}
        <AccordionItem>
          <AccordionButton px={0}>
            <Box flex="1" textAlign="left" fontSize="sm" fontWeight="semibold">
              Payload Builder
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel pb={4} px={0}>
            <HStack justify="space-between" mb={2}>
              <Text fontSize="sm" fontWeight="medium">Parameter Builder</Text>
              <Button
                size="xs"
                colorScheme="blue"
                leftIcon={<AddIcon boxSize="10px" />}
                onClick={() => {
                  const newParams = [...(nodeConfig.queryParams || []), { key: '', value: '' }];
                  setNodeConfig({ ...nodeConfig, queryParams: newParams });
                }}
              >
                Add
              </Button>
            </HStack>

            <VStack spacing={2} align="stretch">
              {(nodeConfig.queryParams || []).map((param, index) => (
                <HStack key={index} spacing={2}>
                  <Input
                    flex={1}
                    size="sm"
                    placeholder="Key"
                    value={param.key || ''}
                    onChange={(e) => {
                      const newParams = [...(nodeConfig.queryParams || [])];
                      newParams[index] = { ...newParams[index], key: e.target.value };
                      setNodeConfig({ ...nodeConfig, queryParams: newParams });
                    }}
                  />
                  <Box position="relative" flex={1}>
                    <Input
                      size="sm"
                      placeholder="Value or {{variable}}"
                      value={param.value || ''}
                      fontFamily="mono"
                      pr="8"
                      onChange={(e) => {
                        const newParams = [...(nodeConfig.queryParams || [])];
                        newParams[index] = { ...newParams[index], value: e.target.value };
                        setNodeConfig({ ...nodeConfig, queryParams: newParams });
                      }}
                    />
                    <IconButton
                      aria-label="Pick variable"
                      icon={<LuBraces />}
                      size="xs"
                      variant="ghost"
                      color="var(--brand-primary)"
                      _hover={{ bg: 'blue.50' }}
                      position="absolute"
                      right={1}
                      top="50%"
                      transform="translateY(-50%)"
                      zIndex={1}
                      onClick={() => openVarPicker('queryParam', index)}
                    />
                  </Box>
                  <IconButton
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    icon={<DeleteIcon />}
                    aria-label="Remove param"
                    onClick={() => {
                      const newParams = (nodeConfig.queryParams || []).filter((_, i) => i !== index);
                      setNodeConfig({ ...nodeConfig, queryParams: newParams });
                    }}
                  />
                </HStack>
              ))}
              {(!nodeConfig.queryParams || nodeConfig.queryParams.length === 0) && (
                <Box textAlign="center" py={3} border="2px dashed" borderColor="gray.200" borderRadius="md">
                  <Text fontSize="xs" color="gray.400">No query parameters added yet</Text>
                </Box>
              )}
            </VStack>
          </AccordionPanel>
        </AccordionItem>

        {/* ===== SECTION 4: Request Headers ===== */}
        <AccordionItem>
          <AccordionButton px={0}>
            <Box flex="1" textAlign="left" fontSize="sm" fontWeight="semibold">
              Request Headers
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel pb={4} px={0}>
            <HStack justify="space-between" mb={2}>
              <Text fontSize="sm">Custom Headers</Text>
              <Button size="xs" variant="ghost" leftIcon={<AddIcon boxSize="10px" />} onClick={addHeader}>
                Add
              </Button>
            </HStack>
            <VStack spacing={2} align="stretch">
              {headers.map((header, index) => (
                <HStack key={index} spacing={2}>
                  <Input
                    flex={1}
                    size="sm"
                    placeholder="Header Key"
                    value={header.key || ''}
                    onChange={(e) => updateHeader(index, 'key', e.target.value)}
                  />
                  <Input
                    flex={1}
                    size="sm"
                    placeholder="Value"
                    value={header.value || ''}
                    fontFamily="mono"
                    onChange={(e) => updateHeader(index, 'value', e.target.value)}
                  />
                  <IconButton
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    icon={<DeleteIcon />}
                    aria-label="Remove header"
                    onClick={() => removeHeader(index)}
                  />
                </HStack>
              ))}
            </VStack>
          </AccordionPanel>
        </AccordionItem>

        {/* ===== SECTION 5: Request Body ===== */}
        {(nodeConfig.apiMethod === 'POST' || nodeConfig.apiMethod === 'PUT' || nodeConfig.apiMethod === 'PATCH') && (
          <AccordionItem>
            <AccordionButton px={0}>
              <Box flex="1" textAlign="left" fontSize="sm" fontWeight="semibold">
                Request Body
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={4} px={0}>
              {/* <FormControl mb={3}>
                <FormLabel fontSize="sm">Body Type</FormLabel>
                <Select
                  size="sm"
                  value={nodeConfig.bodyType || 'json'}
                  onChange={(e) => setNodeConfig({ ...nodeConfig, bodyType: e.target.value })}
                >
                  <option value="none">None</option>
                  <option value="json">JSON</option>
                  <option value="formdata">Form URL Encoded</option>
                  <option value="raw">Raw</option>
                </Select>
              </FormControl> */}
              <JSONBodyBuilder
                value={nodeConfig.apiPayload || ''}
                onChange={(val) => setNodeConfig({ ...nodeConfig, apiPayload: val })}
                onOpenVariablePicker={(cb) => openVarPicker('bodyField', 0, cb)}
              />
              {/* {nodeConfig.bodyType === 'json' && (
                
              )} */}

              {/* {nodeConfig.bodyType === 'raw' && (
                <FormControl>
                  <HStack justify="space-between" mb={1}>
                    <FormLabel fontSize="sm" mb={0}>Raw Body</FormLabel>
                    <IconButton
                      aria-label="Insert variable"
                      icon={<LuBraces />}
                      size="xs"
                      variant="ghost"
                      color="var(--brand-primary)"
                      _hover={{ bg: 'blue.50' }}
                      onClick={() => openVarPicker('body', 0)}
                    />
                  </HStack>
                  <Textarea
                    size="sm"
                    value={nodeConfig.apiPayload || ''}
                    onChange={(e) => setNodeConfig({ ...nodeConfig, apiPayload: e.target.value })}
                    placeholder="Raw request body..."
                    fontFamily="mono"
                    fontSize="xs"
                    rows={8}
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Use <Code fontSize="xs">{`{{variable}}`}</Code> syntax for dynamic values
                  </Text>
                </FormControl>
              )} */}
            </AccordionPanel>
          </AccordionItem>
        )}

        {/* ===== SECTION 6: Response Handling (Sync only) ===== */}
        {nodeConfig.executionMode === 'sync' && (
          <AccordionItem>
            <AccordionButton px={0}>
              <Box flex="1" textAlign="left" fontSize="sm" fontWeight="semibold">
                Response Handling
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={4} px={0}>
              <VStack spacing={3} align="stretch">
                <HStack justify="space-between">
                  <Text fontSize="sm">Response Mapping</Text>
                  <Button size="xs" variant="ghost" leftIcon={<AddIcon boxSize="10px" />} onClick={addResponseMapping}>
                    Add
                  </Button>
                </HStack>
                {responseMapping.map((mapping, index) => (
                  <HStack key={index} spacing={2}>
                    <Input
                      size="sm"
                      placeholder="Response Field (e.g., data.user_id)"
                      value={mapping.responseField || ''}
                      fontFamily="mono"
                      onChange={(e) => updateResponseMapping(index, 'responseField', e.target.value)}
                    />
                    <Input
                      size="sm"
                      placeholder="Save As Variable"
                      value={mapping.saveAs || ''}
                      fontFamily="mono"
                      onChange={(e) => updateResponseMapping(index, 'saveAs', e.target.value)}
                    />
                    <IconButton
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      icon={<DeleteIcon />}
                      aria-label="Remove mapping"
                      onClick={() => removeResponseMapping(index)}
                    />
                  </HStack>
                ))}
                <Text fontSize="xs" color="gray.500">
                  Extract specific fields from API response for use in subsequent nodes
                </Text>

                <Divider />

                <Text fontSize="sm" fontWeight="medium">HTTP Status Handling</Text>
                <Box p={2} bg="green.50" borderRadius="md" border="1px" borderColor="green.200">
                  <HStack justify="space-between">
                    <Text fontSize="sm">2xx → Success Path</Text>
                    <Tag colorScheme="green" fontSize="xs">Default</Tag>
                  </HStack>
                </Box>
                <Box p={2} bg="yellow.50" borderRadius="md" border="1px" borderColor="yellow.200">
                  <HStack justify="space-between">
                    <Text fontSize="sm">4xx → Client Error Path</Text>
                    <Switch
                      size="sm"
                      isChecked={nodeConfig.handle4xx || false}
                      onChange={(e) => setNodeConfig({ ...nodeConfig, handle4xx: e.target.checked })}
                    />
                  </HStack>
                </Box>
                <Box p={2} bg="red.50" borderRadius="md" border="1px" borderColor="red.200">
                  <HStack justify="space-between">
                    <Text fontSize="sm">5xx → Retry / Fail Path</Text>
                    <Switch
                      size="sm"
                      isChecked={nodeConfig.handle5xx || false}
                      onChange={(e) => setNodeConfig({ ...nodeConfig, handle5xx: e.target.checked })}
                    />
                  </HStack>
                </Box>
              </VStack>
            </AccordionPanel>
          </AccordionItem>
        )}

        {/* ===== SECTION 7: Retry & Timeout ===== */}
        <AccordionItem>
          <AccordionButton px={0}>
            <Box flex="1" textAlign="left" fontSize="sm" fontWeight="semibold">
              Retry & Timeout
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel pb={4} px={0}>
            <HStack justify="space-between" mb={3}>
              <Box>
                <Text fontSize="sm">Enable Retry</Text>
                <Text fontSize="xs" color="gray.500">Automatically retry failed requests</Text>
              </Box>
              <Switch
                size="sm"
                isChecked={nodeConfig.retryEnabled || false}
                onChange={(e) => setNodeConfig({ ...nodeConfig, retryEnabled: e.target.checked })}
              />
            </HStack>

            {nodeConfig.retryEnabled && (
              <VStack spacing={3} align="stretch">
                <FormControl>
                  <FormLabel fontSize="sm">Retry Attempts</FormLabel>
                  <Input
                    size="sm"
                    type="number"
                    value={nodeConfig.retryAttempts || 3}
                    onChange={(e) => setNodeConfig({ ...nodeConfig, retryAttempts: parseInt(e.target.value) || 3 })}
                    min={1}
                    max={10}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">Retry Strategy</FormLabel>
                  <Select
                    size="sm"
                    value={nodeConfig.retryStrategy || 'exponential'}
                    onChange={(e) => setNodeConfig({ ...nodeConfig, retryStrategy: e.target.value })}
                  >
                    <option value="fixed">Fixed Interval</option>
                    <option value="exponential">Exponential Backoff</option>
                  </Select>
                </FormControl>
              </VStack>
            )}
          </AccordionPanel>
        </AccordionItem>

        {/* ===== SECTION 8: API Payload Sample ===== */}
        <AccordionItem>
          <AccordionButton px={0}>
            <Box flex="1" textAlign="left" fontSize="sm" fontWeight="semibold">
              <HStack>
                <InfoIcon boxSize="12px" color="gray.400" />
                <Text>API Payload Sample</Text>
              </HStack>
            </Box>
            {generatedFields.length > 0 ? (
              <Tag colorScheme="green" fontSize="10px" mr={2}>Defined</Tag>
            ) : (
              <Tag colorScheme="gray" fontSize="10px" mr={2}>Not Set</Tag>
            )}
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel pb={4} px={0}>
            <VStack spacing={3} align="stretch">
              <Text fontSize="xs" color="gray.500">
                Define expected API response structure so variables can be used in flow conditions and mappings.
              </Text>

              <FormControl>
                <FormLabel fontSize="sm">Paste Sample JSON Response</FormLabel>
                <Textarea
                  size="sm"
                  value={sampleJson}
                  onChange={(e) => {
                    setSampleJson(e.target.value);
                    setNodeConfig({ ...nodeConfig, sampleJson: e.target.value });
                  }}
                  placeholder={'{\n  "user_id": "12345",\n  "name": "John Doe",\n  "balance": 150.50,\n  "status": "active"\n}'}
                  fontFamily="mono"
                  fontSize="xs"
                  rows={8}
                />
              </FormControl>

              <Button size="sm" colorScheme="blue" onClick={generateFieldsFromJson}>
                ✨ Generate Variables
              </Button>

              {generatedFields.length > 0 && (
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" mb={2}>
                    Generated Variables ({generatedFields.length})
                  </Text>
                  <Box border="1px" borderColor="gray.200" borderRadius="md" p={3} maxH="250px" overflowY="auto">
                    <VStack spacing={2} align="stretch">
                      {generatedFields.map((field, idx) => (
                        <Box key={idx} borderBottom="1px" borderColor="gray.100" pb={2}>
                          <HStack justify="space-between" mb={1}>
                            <Code fontSize="xs" color="blue.600" wordBreak="break-all">
                              {`{{api.${nodeConfig.responseNamespace || 'api_response'}${field.jsonPath}}}`}
                            </Code>
                            <Tag fontSize="9px" variant="outline" flexShrink={0}>{field.type}</Tag>
                          </HStack>
                          <Text fontSize="10px" color="gray.500">
                            Example: <Code fontSize="10px">{field.example}</Code>
                          </Text>
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                </Box>
              )}

              <Divider />

              <FormControl>
                <FormLabel fontSize="sm">Save Response As</FormLabel>
                <Input
                  size="sm"
                  value={nodeConfig.responseNamespace || 'api_response'}
                  onChange={(e) => setNodeConfig({ ...nodeConfig, responseNamespace: e.target.value })}
                  placeholder="e.g., api_response"
                  fontFamily="mono"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Response variables available under:{' '}
                  <Code fontSize="10px">{`{{api.${nodeConfig.responseNamespace || 'api_response'}}}`}</Code>
                </Text>
              </FormControl>
            </VStack>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>

      {/* Action Buttons */}
      <Divider />
      <Button size="sm" colorScheme="green" onClick={updateNodeData} w="full">
        Update Configuration
      </Button>

      <VariablePickerDrawer
        isOpen={varPickerOpen}
        onClose={() => setVarPickerOpen(false)}
        onSelect={handleVarSelect}
        flowNodes={nodes}
      />
    </VStack>
  );
}